import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { DeadlineBadge } from "@/components/common/Badges";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fdate } from "@/lib/format";

const ALL = "all";
const TYPES = ["Contrat", "Assurance", "Contrôle technique", "Entretien", "Pneus", "Permis", "Restitution"];

export default function Deadlines() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [fType, setFType] = useState(TYPES.includes(params.get("type")) ? params.get("type") : ALL);
  const [fStatus, setFStatus] = useState(ALL);
  const maxDays = params.get("jours") ? Number(params.get("jours")) : null;

  useEffect(() => { api.get("/deadlines").then((r) => setRows(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter((r) => (fType === ALL || r.type === fType) && (fStatus === ALL || r.status === fStatus) && (maxDays == null || (r.days != null && r.days < maxDays)));
  }, [rows, fType, fStatus, maxDays]);

  const counts = useMemo(() => {
    const c = { critical: 0, warning: 0, ok: 0 };
    (rows || []).forEach((r) => { if (c[r.status] != null) c[r.status]++; });
    return c;
  }, [rows]);

  return (
    <div>
      <PageHeader title="Échéances" subtitle="Toutes les échéances de la flotte, classées par urgence." />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[["critical", "Urgent (< 15 j)", "bg-red-50 border-red-200 text-red-800"], ["warning", "Bientôt (15-30 j)", "bg-amber-50 border-amber-200 text-amber-800"], ["ok", "À jour (> 30 j)", "bg-emerald-50 border-emerald-200 text-emerald-800"]].map(([k, label, cls]) => (
          <div key={k} className={`rounded-2xl border p-5 ${cls}`}>
            <div className="font-heading text-3xl font-bold">{counts[k]}</div>
            <div className="mt-1 text-sm font-medium">{label}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={fType} onValueChange={setFType}><SelectTrigger className="w-52 bg-white" data-testid="deadline-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tous les types</SelectItem>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="w-52 bg-white" data-testid="deadline-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Toutes urgences</SelectItem><SelectItem value="critical">Urgent</SelectItem><SelectItem value="warning">Bientôt</SelectItem><SelectItem value="ok">À jour</SelectItem></SelectContent></Select>
        {maxDays != null && <span className="inline-flex items-center rounded-full border border-[#A5894E]/40 bg-[#F4EFE3] px-3 py-1 text-xs font-medium text-[#6b5626]" data-testid="deadline-filter-days">Moins de {maxDays} jours<button onClick={() => { const p = new URLSearchParams(params); p.delete("jours"); setParams(p); }} className="ml-1.5 rounded-full px-1 hover:bg-[#A5894E]/20" data-testid="deadline-filter-days-clear">×</button></span>}
      </div>

      {rows === null ? <div className="space-y-2">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
        : filtered.length === 0 ? <EmptyState icon={CalendarClock} title="Aucune échéance" />
        : (
          <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
            {filtered.map((r, i) => (
              <div key={i} className="flex items-center gap-4 border-b border-[#EDF0F5] px-4 py-3 last:border-0 hover:bg-[#F5F7FA]/60" data-testid="deadline-row">
                <DeadlineBadge level={r.status}>{r.type}</DeadlineBadge>
                <div className="min-w-0 flex-1"><p className="truncate font-medium text-[#18221F]">{r.label}</p><p className="truncate text-xs text-[#8695AB]">{r.vehicle || "—"}</p></div>
                <div className="text-right"><div className="text-sm font-medium text-[#18221F]">{fdate(r.date)}</div><div className="text-xs text-[#8695AB]">{r.days != null ? (r.days < 0 ? `${Math.abs(r.days)} j dépassé` : `dans ${r.days} j`) : ""}</div></div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
